import React, { useState } from 'react';
import { Typography, TextField, Button, Alert, Box } from '@mui/material';
import { DateField, LoadingButton } from '@routes-plan/ui';
import StandardModal from '../../../components/modales/StandardModal';
import { Loan } from '../../../models/loan';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';

interface UpdateDateModalProps {
  open: boolean;
  onClose: () => void;
  client: Loan | null;
  clientId?: number;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;  
}



const UpdateDateModal: React.FC<UpdateDateModalProps> = ({ open, onClose, client, clientId }) => {
  const t = useT();
  const [newDate, setNewDate] = useState(''); // Estado para la fecha nueva
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAccept = async () => {
    setIsSaving(true);

    if (!newDate) {
      setErrorMessage(t('loanActions.dateRequired'));
      setIsSaving(false);
      return;
    }

    const formData = {
      loanId: Number(clientId),
      newDate, // Enviar la nueva fecha al servidor
    };

    try {
      // Aquí iría la lógica para enviar los datos al servidor
      setErrorMessage('');
      onClose(); // Cerrar el modal después de guardar
    } catch (error) {
      setErrorMessage(t('loanActions.updateDateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setNewDate('');
    setErrorMessage('');
    onClose();
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('loanActions.updateDateTitle')}
      subtitle={t('loanActions.loanNumber', { loan: clientId ?? '' })}
      maxWidth={500}
      loading={isSaving}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <LoadingButton variant="contained" onClick={handleAccept} loading={isSaving}>
            {t('common.save')}
          </LoadingButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('loanActions.updateDateConfirm', { loan: clientId ?? '' })}
      </Typography>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t('loanActions.currentStartDate')}
          value={client?.date ?? ''}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
        <DateField
          fullWidth
          size="small"
          label={t('loanActions.newDate')}
          value={newDate || null}
          onChange={(v) => setNewDate(v ?? '')}
          max={nowOperationalDateString()}
        />
      </Box>
    </StandardModal>
  );
};

export default UpdateDateModal;